import { Alert, tokens } from "@blurb/codex-react";
import { Box, createStyles, Group } from "@mantine/core";
import { NotificationProps, showNotification } from "@mantine/notifications";
import { ReactNode, useCallback } from "react";
import { IconV3 } from "../icons/IconV3";
import { RpiBodyText } from "./typography/RpiBodyText";

const useSuccessStyles = createStyles((theme) => ({
  root: {
    background: theme.other.semanticColors.successBackground,
    color: theme.other.semanticColors.textInverse,
    padding: `0px ${theme.spacing.sm}px`,
    border: "none",
    height: theme.other.sizes.md,
    justifySelf: "center",
    maxWidth: "fit-content",
    "&::before": {
      display: "none",
    },
  },
  icon: {
    display: "none",
  },
  body: {
    margin: 0,
    gap: theme.spacing.sm,
  },
  description: {
    color: "inherit",
    gap: theme.spacing.sm,
  },
}));

export function useShowSuccessToast() {
  const { classes } = useSuccessStyles();
  const showSuccessToast = useCallback(
    (message: string) => {
      showNotification({
        message: (
          <Group sx={{ gap: "inherit", flexWrap: "nowrap" }}>
            <IconV3 icon="check_circle" color="inherit" size={20} />
            <RpiBodyText>{message}</RpiBodyText>
          </Group>
        ),
        disallowClose: true,
        autoClose: 4000,
        classNames: classes,
      });
    },
    [classes]
  );

  return showSuccessToast;
}

const useActionableSuccessStyles = createStyles((theme) => ({
  root: {
    background: tokens.color.semantic.bg.success,
    color: tokens.color.semantic.text.inverse,
    padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
    border: "none",
    justifySelf: "center",
    "&::before": {
      display: "none",
    },
  },
  icon: {
    display: "none",
  },
  body: {
    margin: 0,
    gap: theme.spacing.sm,
  },
  description: {
    color: "inherit",
    gap: theme.spacing.sm,
    "& a": {
      color: "inherit",
      textDecoration: "underline",
      "&:hover": {
        color: "inherit",
      },
    },
  },
  closeButton: {
    color: "inherit",
    "&:hover": {
      background: tokens.color.semantic.bg.successHover,
    },
  },
}));

export function useShowActionableSuccessToast() {
  const { classes } = useActionableSuccessStyles();
  return useCallback(
    (content: ReactNode) => {
      showNotification({
        message: (
          <Group sx={{ gap: "inherit", flexWrap: "nowrap" }}>
            <IconV3 icon="check_circle" color="inherit" size={20} />
            <RpiBodyText>{content}</RpiBodyText>
          </Group>
        ),
        disallowClose: false,
        autoClose: false,
        classNames: classes,
      });
    },
    [classes]
  );
}

const useInfoToastStyles = createStyles(() => ({
  root: {
    background: "transparent",
    padding: 0,
    border: "none",
    boxShadow: "none",
    maxWidth: "fit-content",
    marginTop: 80,
    marginLeft: "auto",
    marginRight: "auto",
    "&::before": {
      display: "none",
    },
  },
  icon: {
    display: "none",
  },
  body: {
    margin: 0,
  },
  description: {
    color: "inherit",
  },
}));

export function useShowInfoToast() {
  const { classes } = useInfoToastStyles();
  const showInfoToast = useCallback(
    (message: string) => {
      showNotification({
        message: (
          <Box sx={{ "& > div": { backgroundColor: tokens.color.semantic.bg.info, width: "fit-content" } }}>
            <Alert type="info" size="small">
              <Group sx={{ gap: tokens.spacing[2], color: tokens.color.semantic.text.inverse }}>
                <IconV3 icon="info" color="inherit" size={20} />
                <RpiBodyText>{message}</RpiBodyText>
              </Group>
            </Alert>
          </Box>
        ),
        disallowClose: true,
        autoClose: 4000,
        classNames: classes,
      });
    },
    [classes]
  );

  return showInfoToast;
}

const useErrorToastStyles = createStyles((theme) => ({
  root: {
    background: tokens.color.semantic.bg.danger,
    color: tokens.color.semantic.text.inverse,
    padding: `0px ${theme.spacing.sm}px`,
    border: "none",
    height: theme.other.sizes.md,
    justifySelf: "center",
    maxWidth: "fit-content",
    "&::before": {
      display: "none",
    },
  },
  icon: {
    display: "none",
  },
  body: {
    margin: 0,
    gap: theme.spacing.sm,
  },
  description: {
    color: "inherit",
    gap: theme.spacing.sm,
  },
}));

export function useShowErrorToast() {
  const { classes } = useErrorToastStyles();
  return useCallback(
    (message: string) => {
      showNotification({
        message: (
          <Group sx={{ gap: "inherit", flexWrap: "nowrap" }}>
            <IconV3 icon="error" color="inherit" size={20} />
            <RpiBodyText>{message}</RpiBodyText>
          </Group>
        ),
        disallowClose: true,
        autoClose: 4000,
        classNames: classes,
      });
    },
    [classes]
  );
}

export function useToasts() {
  return {
    showSuccessToast: useShowSuccessToast(),
    showInfoToast: useShowInfoToast(),
    showErrorToast: useShowErrorToast(),
    showActionableSuccessToast: useShowActionableSuccessToast(),
  };
}

/**
 * @deprecated
 * This wrapper makes sure the old notifications look ok inside of the updated NotificationProvider.
 */
export function showOldNotification(props: NotificationProps) {
  showNotification({ ...props, sx: { justifySelf: "center", width: 440 } });
}
